import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useGameStore } from "../store/gameStore";
import { useAuthStore } from "../store/authStore";
import { useLobbyConnection } from "../hooks/useLobby";
import { wsActions } from "../lib/ws";
import type { GameStateView } from "../lib/types";
import { PlayingCard } from "../components/card/PlayingCard";
import { Badge, Button, Hearts, Modal, Panel } from "../components/ui";

export function GameScreen() {
  const { code = "" } = useParams();
  useLobbyConnection(code);
  const state = useGameStore((s) => s.gameState);
  const meId = useAuthStore((s) => s.userId);
  const pendingAce = useGameStore((s) => s.pendingAceCardId);
  const navigate = useNavigate();

  // When the owner starts a new game after a match ("Nuova partita"), the
  // server flips the lobby back to LOBBY and pushes it to the whole room:
  // every client must follow back to the waiting room instead of showing a
  // dead game table (F2 fix).
  useEffect(() => {
    if (state && state.game_status === "LOBBY") {
      navigate(`/lobby/${state.code}`, { replace: true });
    }
  }, [state, navigate]);

  if (!state) {
    return <div className="flex h-[60vh] items-center justify-center text-ink-dim">Caricamento partita…</div>;
  }

  // Terminal screens
  if (state.game_status === "ENDED") return <WinnerScreen state={state} />;

  return (
    <div className="mx-auto max-w-7xl px-3 py-4">
      <InfoBar state={state} />
      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <TableArea state={state} meId={meId ?? ""} pendingAce={pendingAce} />
        <PlayersPanel state={state} meId={meId ?? ""} />
      </div>
      <AceDiamondsModal />
    </div>
  );
}

function InfoBar({ state }: { state: GameStateView }) {
  const phase = state.phase === "DECLARATION" ? "Dichiarazione" : state.phase === "PLAYING" ? "Gioco" : "Fine round";
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
      <Chip label={`Round ${state.round_number}`} />
      <Chip label={`${state.cards_per_player} carte`} />
      <Chip label={`Mano ${state.current_trick_number}`} />
      <Badge tone={state.phase === "PLAYING" ? "emerald" : "brass"}>{phase}</Badge>
      {state.is_one_card_round && <Badge tone="brass">Round da 1 carta</Badge>}
      <span className="ml-auto font-mono text-ink-faint">{state.code}</span>
    </div>
  );
}

function Chip({ label }: { label: string }) {
  return <span className="rounded-full border border-line px-2.5 py-1 text-ink-dim">{label}</span>;
}

function TableArea({ state, meId, pendingAce }: {
  state: GameStateView;
  meId: string;
  pendingAce: string | null;
}) {
  const myTurn = state.current_player_id === meId;
  return (
    <div className="flex flex-col gap-4">
      <div className="felt relative min-h-[340px] rounded-xl2 p-6">
        <TrickZone state={state} />
      </div>
      <Hand state={state} meId={meId} myTurn={myTurn} pendingAce={pendingAce} />
      <ActionPanel state={state} meId={meId} />
    </div>
  );
}

function TrickZone({ state }: { state: GameStateView }) {
  const entries = Object.entries(state.current_trick);
  if (entries.length === 0) {
    return (
      <div className="flex h-full min-h-[220px] items-center justify-center">
        <span className="font-display text-lg text-ink-faint/40 italic">Nessuna carta sul tavolo</span>
      </div>
    );
  }
  const playerById = (id: string) => state.players.find((p) => p.id === id);
  return (
    <div className="flex h-full min-h-[220px] flex-col items-center justify-center gap-3">
      <div className="flex flex-wrap items-center justify-center gap-3">
        <AnimatePresence>
          {entries.map(([pid, card]) => {
            const p = playerById(pid);
            return (
              <motion.div
                key={pid}
                initial={{ opacity: 0, y: -40, scale: 0.85, rotate: -6 }}
                animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ type: "spring", stiffness: 320, damping: 24 }}
                className="flex flex-col items-center gap-1"
              >
                <PlayingCard value={card.value} suit={card.suit} playedAsHigh={card.played_as_high} size="md" />
                <span className="text-[0.7rem] text-ink-dim">{p?.name}</span>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Hand({ state, meId, myTurn, pendingAce }: {
  state: GameStateView;
  meId: string;
  myTurn: boolean;
  pendingAce: string | null;
}) {
  const me = state.players.find((p) => p.id === meId);
  const hand = me?.hand ?? [];
  const canPlay = myTurn && state.phase === "PLAYING";

  // 1-card round: the player's own hand is hidden (ADR-011); show a back.
  if (state.is_one_card_round && me && hand.length === 0 && me.hand_size > 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-2">
        <span className="label-caps">La tua carta</span>
        <PlayingCardHidden />
      </div>
    );
  }

  if (hand.length === 0) {
    return <div className="py-4 text-center text-sm text-ink-faint">Nessuna carta in mano</div>;
  }

  return (
    <div className="flex flex-col items-center gap-2 py-2">
      <span className="label-caps">La tua mano</span>
      <div className="flex flex-wrap justify-center gap-1.5">
        {hand.map((c) => (
          <PlayingCard
            key={c.id}
            value={c.value}
            suit={c.suit}
            playedAsHigh={c.played_as_high}
            size="md"
            disabled={!canPlay}
            selected={pendingAce === c.id}
            onClick={() => {
              if (!canPlay) return;
              if (c.is_ace_of_diamonds) {
                useGameStore.getState().setPendingAce(c.id); // open the high/low modal
              } else {
                wsActions.play(c.id, false, crypto.randomUUID());
              }
            }}
          />
        ))}
      </div>
      {!canPlay && <span className="text-xs text-ink-faint">In attesa del tuo turno…</span>}
    </div>
  );
}

function PlayingCardHidden() {
  return (
    <div className="relative">
      <PlayingCard value={"ACE"} suit={"DIAMONDS"} size="md" className="opacity-90" />
      <div className="absolute inset-0 rounded-card bg-surface/85 backdrop-blur-sm flex items-center justify-center">
        <span className="font-display text-3xl text-brass/70">?</span>
      </div>
    </div>
  );
}

// --- Declaration + play action panel -------------------------------

function ActionPanel({ state, meId }: { state: GameStateView; meId: string }) {
  if (state.phase === "DECLARATION") {
    return state.is_one_card_round ? <OneCardDeclarePanel state={state} meId={meId} /> : <DeclarePanel state={state} meId={meId} />;
  }
  if (state.phase === "PLAYING") {
    const myTurn = state.current_player_id === meId;
    const cur = state.players.find((p) => p.id === state.current_player_id);
    return (
      <Panel className="flex items-center justify-between">
        <span className="text-sm text-ink-dim">
          {myTurn ? "È il tuo turno — gioca una carta." : `In attesa di ${cur?.name ?? "…"}…`}
        </span>
        {myTurn && <Badge tone="emerald">Tuo turno</Badge>}
      </Panel>
    );
  }
  // ENDED -> owner-driven next round
  const isOwner = meId === state.owner_id;
  return (
    <Panel className="flex items-center justify-between">
      <span className="text-sm text-ink-dim">Round concluso.</span>
      {isOwner ? (
        <Button onClick={() => wsActions.nextRound()}>Prossimo round</Button>
      ) : (
        <span className="text-xs text-ink-faint">In attesa dell'host…</span>
      )}
    </Panel>
  );
}

function DeclarePanel({ state, meId }: { state: GameStateView; meId: string }) {
  const active = state.players.filter((p) => !p.eliminated);
  const declared = active.filter((p) => p.declared_tricks >= 0);
  const isMyTurn = state.current_player_id === meId;
  const me = state.players.find((p) => p.id === meId);
  const alreadyDeclared = me?.declared_tricks !== undefined && me.declared_tricks >= 0;
  // The "last declarer" hint only applies to whoever has NOT declared yet.
  const isLastDeclarer = !alreadyDeclared && declared.length === active.length - 1;
  const sumOthers = declared.filter((p) => p.id !== meId).reduce((a, p) => a + p.declared_tricks, 0);
  const forbidden = isLastDeclarer ? state.cards_per_player - sumOthers : null;

  return (
    <Panel>
      <div className="mb-3 flex items-center justify-between">
        <span className="label-caps">Dichiara i trick che vincerai</span>
        {isMyTurn && !alreadyDeclared && <Badge tone="emerald">Tuo turno</Badge>}
      </div>
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: state.cards_per_player + 1 }, (_, n) => n).map((n) => {
          const disabled = !isMyTurn || alreadyDeclared || forbidden === n;
          return (
            <button
              key={n}
              disabled={disabled}
              onClick={() => wsActions.declare(n)}
              className={`h-12 w-12 rounded-xl border text-lg font-semibold transition-colors ${
                forbidden === n
                  ? "border-crimson-dim text-crimson/40 line-through"
                  : "border-line text-ink hover:border-brass/50 hover:bg-surface-2"
              } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              {n}
            </button>
          );
        })}
      </div>
      {forbidden !== null && (
        <p className="mt-2 text-xs text-ink-faint">L'ultimo dichiarante non può portare il totale a {state.cards_per_player}.</p>
      )}
      {alreadyDeclared && <p className="mt-2 text-xs text-emerald">Hai dichiarato {me?.declared_tricks}.</p>}
    </Panel>
  );
}

function OneCardDeclarePanel({ state, meId }: { state: GameStateView; meId: string }) {
  const isMyTurn = state.current_player_id === meId;
  const me = state.players.find((p) => p.id === meId);
  const alreadyDeclared = !!me?.one_card_declaration;
  const active = state.players.filter((p) => !p.eliminated);
  const declared = active.filter((p) => p.one_card_declaration);
  // Same as DeclarePanel: the hint is only meaningful before I declare.
  const isLastDeclarer = !alreadyDeclared && declared.length === active.length - 1;
  const winCount = declared.filter((p) => p.id !== meId && p.one_card_declaration === "win").length;
  const forbiddenWin = isLastDeclarer && winCount === 0;
  const forbiddenLose = isLastDeclarer && winCount === 1;

  return (
    <Panel>
      <div className="mb-3 flex items-center justify-between">
        <span className="label-caps">Round da 1 carta — vinci o perdi?</span>
        {isMyTurn && !alreadyDeclared && <Badge tone="emerald">Tuo turno</Badge>}
      </div>
      <div className="flex gap-3">
        <button
          disabled={!isMyTurn || alreadyDeclared || forbiddenWin}
          onClick={() => wsActions.declareOne("win")}
          className="btn flex-1 border border-line text-ink hover:border-emerald/50 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Vinco
        </button>
        <button
          disabled={!isMyTurn || alreadyDeclared || forbiddenLose}
          onClick={() => wsActions.declareOne("lose")}
          className="btn flex-1 border border-line text-ink hover:border-crimson/50 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Perdo
        </button>
      </div>
      {alreadyDeclared && <p className="mt-2 text-xs text-emerald">Hai dichiarato: {me?.one_card_declaration === "win" ? "vinco" : "perdo"}.</p>}
    </Panel>
  );
}

// --- Players panel ------------------------------------------------

function PlayersPanel({ state, meId }: { state: GameStateView; meId: string }) {
  return (
    <Panel className="p-0 overflow-hidden">
      <div className="border-b border-line/60 px-4 py-3">
        <h2 className="label-caps">Giocatori</h2>
      </div>
      <ul className="divide-y divide-line/60">
        {state.players.map((p) => (
          <li key={p.id} className={`px-4 py-3 ${p.id === meId ? "bg-surface-2/40" : ""}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${p.connected ? "bg-emerald" : "bg-ink-faint"}`} />
                <span className={`text-sm ${p.eliminated ? "text-ink-faint line-through" : "text-ink"}`}>{p.name}</span>
                {p.id === state.owner_id && <Badge tone="brass">host</Badge>}
                {p.is_current_turn && <Badge tone="emerald">turno</Badge>}
              </div>
              <Hearts lives={p.lives} />
            </div>
            <div className="mt-1.5 flex items-center justify-between text-xs text-ink-dim">
              <span>
                {state.phase === "DECLARATION"
                  ? p.one_card_declaration
                    ? p.one_card_declaration === "win" ? "vinco" : "perdo"
                    : p.declared_tricks >= 0 ? `dichiara ${p.declared_tricks}` : "—"
                  : `${p.won_tricks}/${p.declared_tricks >= 0 ? p.declared_tricks : "?"}`}
              </span>
              {p.elo_rating != null && <span className="font-mono">{p.elo_rating}</span>}
            </div>
            {/* ADR-011: in the 1-card round the server reveals opponents'
                single cards (hand != null) while hiding our own. Render
                whatever is sent — normal rounds arrive as hand: null. */}
            {p.id !== meId && p.hand && p.hand.length > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[0.7rem] text-ink-faint">in mano</span>
                <div className="flex gap-1">
                  {p.hand.map((c) => (
                    <PlayingCard
                      key={c.id}
                      value={c.value}
                      suit={c.suit}
                      playedAsHigh={c.played_as_high}
                      size="sm"
                    />
                  ))}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

// --- Winner -------------------------------------------------------

function WinnerScreen({ state }: { state: GameStateView }) {
  const navigate = useNavigate();
  // Primary: the server's winner_id. Fallback for legacy/edge ENDED states
  // persisted without a winner (pre-RULE-023-fix lobbies): mirror the server
  // tie-break (most lives, then table order) so the screen never renders an
  // empty "— ha vinto la partita".
  const winner =
    state.players.find((p) => p.id === state.winner_id) ??
    [...state.players].sort((a, b) => b.lives - a.lives)[0];
  const isOwner = useAuthStore.getState().userId === state.owner_id;
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4 text-center">
      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 14 }}
        className="mb-4 text-6xl"
      >
        ♦
      </motion.div>
      <h1 className="font-display text-4xl text-brass">{winner?.name ?? "—"}</h1>
      <p className="mt-2 text-ink-dim">ha vinto la partita</p>
      <Button
        className="mt-8"
        onClick={() => {
          // Back to the party to play another match. Reset is owner-only: the
          // host flips the lobby back to LOBBY for the whole room (the other
          // clients follow via the LOBBY redirect above), while guests simply
          // return to the waiting room and wait for the host to start again.
          if (isOwner) wsActions.reset();
          navigate(`/lobby/${state.code}`);
        }}
      >
        Torna alla lobby
      </Button>
    </div>
  );
}

// --- Ace of Diamonds high/low modal (driven by pendingAceCardId) ---

export function AceDiamondsModal() {
  const pendingAce = useGameStore((s) => s.pendingAceCardId);
  const setPendingAce = useGameStore((s) => s.setPendingAce);
  return (
    <Modal open={pendingAce !== null} onClose={() => setPendingAce(null)} title="Asso di Quadri">
      <p className="mb-5 text-sm text-ink-dim">Come vuoi giocare l'Asso di Quadri?</p>
      <div className="flex gap-3">
        <button
          className="btn flex-1 flex-col border border-line py-4 hover:border-brass/50"
          onClick={() => {
            if (pendingAce) wsActions.play(pendingAce, false, crypto.randomUUID());
            setPendingAce(null);
          }}
        >
          <span className="text-lg">⬇ Basso</span>
          <span className="text-[0.7rem] text-ink-faint">come gli altri assi</span>
        </button>
        <button
          className="btn-primary flex-1 flex-col py-4"
          onClick={() => {
            if (pendingAce) wsActions.play(pendingAce, true, crypto.randomUUID());
            setPendingAce(null);
          }}
        >
          <span className="text-lg">⬆ Alto</span>
          <span className="text-[0.7rem] opacity-80">sopra il Re</span>
        </button>
      </div>
    </Modal>
  );
}
